import type { DBUserIdentity } from '../../db/database';
import { fetchPublicSummaries } from './api';
import { IdentityRepository } from './repository';
import { IdentityStore } from './store';
import type {
  IdentityVersionRequest,
  PublicSummaryBatchResult,
  UserIdentity,
} from './types';

type IdentitySummaryFetcher = typeof fetchPublicSummaries;

type PendingInvalidation = {
  minProfileVersion: number;
  baseUrl?: string;
  resolve: () => void;
  reject: (error: Error) => void;
};

export class IdentityCoordinator {
  private readonly pendingInvalidations = new Map<number, PendingInvalidation[]>();
  private invalidationDrain: Promise<void> | null = null;

  constructor(
    private readonly ownerUserId: number,
    private readonly repository: IdentityRepository,
    private readonly store: IdentityStore,
    private readonly fetchSummaries: IdentitySummaryFetcher = fetchPublicSummaries,
  ) {}

  async hydrateCache(shouldPublish: () => boolean = () => true): Promise<void> {
    const rows = await this.repository.loadAll();
    // Account switches can supersede an IndexedDB read while it is in flight.
    // The owner-aware application guard prevents a late prior-account result
    // from entering the current reactive Store.
    if (!shouldPublish()) return;
    this.store.publish(rows.map((row) => this.toStoreIdentity(row, null, 'cache')));
  }

  async fetchAndCommit(
    requests: IdentityVersionRequest[],
    baseUrl?: string,
    nextChangeCursor?: number,
  ): Promise<void> {
    const normalized = this.normalizeRequests(requests);
    const results = await this.fetchSummaries(normalized, baseUrl);
    const rows = this.validateCompleteResults(normalized, results);
    const accepted = await this.repository.commit({
      rows,
      ownerUserId: this.ownerUserId,
      nextChangeCursor,
    });

    // Publication is deliberately after the awaited transaction commit. A
    // rejected local write leaves both Store and cursor at their prior state.
    const mediaById = new Map(
      results
        .filter((result) => result.status === 'ok')
        .map((result) => [result.userId, result.profile.avatar] as const),
    );
    this.store.publish(accepted.map((row) => (
      this.toStoreIdentity(row, mediaById.get(row.subjectUserId) ?? null, 'network')
    )));
  }

  async commitReconnectCursor(nextChangeCursor: number): Promise<void> {
    if (!Number.isSafeInteger(nextChangeCursor) || nextChangeCursor < 0) {
      throw new Error('Identity reconnect cursor is invalid');
    }
    // Empty/sparse terminal IM pages still prove that the complete interval up
    // to nextChangeCursor was inspected. Persist that proof transactionally
    // even when there is no identity row to publish to Store.
    await this.repository.commit({
      rows: [],
      ownerUserId: this.ownerUserId,
      nextChangeCursor,
    });
  }

  /**
   * Coalesces realtime IM invalidations into bounded canonical HTTP reads.
   *
   * Multiple notifications for one user retain only the highest requested
   * profile version. A microtask boundary combines bursts, and the drain
   * rechecks Store state before every page so a notification arriving while an
   * earlier request is in flight does not cause a redundant second fetch.
   *
   * `change_cursor` is intentionally not accepted here. Realtime delivery can
   * be sparse or out of order, so only the reconnect-sync response may advance
   * the durable IdentitySyncState cursor.
   */
  refreshInvalidated(
    request: IdentityVersionRequest,
    baseUrl?: string,
  ): Promise<void> {
    this.normalizeRequests([request]);
    if (request.minProfileVersion <= 0) {
      throw new Error('Realtime identity invalidation requires a positive profile version');
    }
    const current = this.store.get(request.userId);
    if (current && current.profileVersion >= request.minProfileVersion) {
      return Promise.resolve();
    }

    const result = new Promise<void>((resolve, reject) => {
      const waiters = this.pendingInvalidations.get(request.userId) ?? [];
      waiters.push({
        minProfileVersion: request.minProfileVersion,
        baseUrl,
        resolve,
        reject,
      });
      this.pendingInvalidations.set(request.userId, waiters);
    });

    if (!this.invalidationDrain) {
      // Promise.resolve creates a microtask batching window without adding a
      // user-visible debounce delay to realtime profile updates.
      this.invalidationDrain = Promise.resolve()
        .then(() => this.drainInvalidations())
        .finally(() => {
          this.invalidationDrain = null;
          // A callback can enqueue work after the drain's final empty check but
          // before `finally`; start another drain so no invalidation is lost.
          if (this.pendingInvalidations.size > 0) {
            void this.refreshDrain();
          }
        });
    }
    return result;
  }

  private refreshDrain(): Promise<void> {
    if (!this.invalidationDrain) {
      this.invalidationDrain = Promise.resolve()
        .then(() => this.drainInvalidations())
        .finally(() => {
          this.invalidationDrain = null;
          if (this.pendingInvalidations.size > 0) void this.refreshDrain();
        });
    }
    return this.invalidationDrain;
  }

  private async drainInvalidations(): Promise<void> {
    while (this.pendingInvalidations.size > 0) {
      const entries = [...this.pendingInvalidations.entries()].slice(0, 200);
      for (const [userId] of entries) this.pendingInvalidations.delete(userId);

      const requests: IdentityVersionRequest[] = [];
      const activeEntries: typeof entries = [];
      for (const entry of entries) {
        const [userId, waiters] = entry;
        // Use an iterative maximum instead of spreading an untrusted-length
        // notification burst into function arguments.
        let minProfileVersion = 0;
        for (const waiter of waiters) {
          minProfileVersion = Math.max(minProfileVersion, waiter.minProfileVersion);
        }
        const current = this.store.get(userId);
        if (current && current.profileVersion >= minProfileVersion) {
          for (const waiter of waiters) waiter.resolve();
          continue;
        }
        requests.push({ userId, minProfileVersion });
        activeEntries.push(entry);
      }
      if (requests.length === 0) continue;

      // One coordinator belongs to one application runtime, so all queued
      // callers normally share a base URL. Keeping it on each waiter makes the
      // dependency explicit and detects accidental cross-runtime reuse.
      const baseUrls = new Set(activeEntries.flatMap(([, waiters]) => (
        waiters.map((waiter) => waiter.baseUrl ?? '')
      )));
      try {
        if (baseUrls.size !== 1) {
          throw new Error('Identity invalidation batch contains conflicting API base URLs');
        }
        await this.fetchAndCommit(requests, activeEntries[0][1][0].baseUrl);
        for (const [, waiters] of activeEntries) {
          for (const waiter of waiters) waiter.resolve();
        }
      } catch (error) {
        const failure = error instanceof Error ? error : new Error(String(error));
        for (const [, waiters] of activeEntries) {
          for (const waiter of waiters) waiter.reject(failure);
        }
      }
    }
  }

  private normalizeRequests(requests: IdentityVersionRequest[]): IdentityVersionRequest[] {
    if (requests.length === 0 || requests.length > 200) {
      throw new Error('Identity page must contain between 1 and 200 users');
    }
    const minimumByUser = new Map<number, number>();
    for (const request of requests) {
      if (!Number.isSafeInteger(request.userId) || request.userId <= 0 ||
          !Number.isSafeInteger(request.minProfileVersion) ||
          request.minProfileVersion < 0) {
        throw new Error('Identity request contains an invalid user/version');
      }
      minimumByUser.set(
        request.userId,
        Math.max(minimumByUser.get(request.userId) ?? 0, request.minProfileVersion),
      );
    }
    return [...minimumByUser].map(([userId, minProfileVersion]) => ({
      userId,
      minProfileVersion,
    }));
  }

  private validateCompleteResults(
    requests: IdentityVersionRequest[],
    results: PublicSummaryBatchResult[],
  ): DBUserIdentity[] {
    if (results.length !== requests.length) {
      throw new Error('Identity batch is incomplete');
    }
    const requestById = new Map(requests.map((request) => [request.userId, request]));
    const seen = new Set<number>();
    const rows: DBUserIdentity[] = [];
    for (const result of results) {
      const request = requestById.get(result.userId);
      if (!request || seen.has(result.userId)) {
        throw new Error('Identity batch contains an unexpected or duplicate user');
      }
      seen.add(result.userId);
      if (result.status === 'ok') {
        const profile = result.profile;
        if (profile.userId !== result.userId ||
            !Number.isSafeInteger(profile.profileVersion) ||
            profile.profileVersion < Math.max(1, request.minProfileVersion) ||
            !profile.displayName) {
          throw new Error('Identity batch contains an invalid canonical profile');
        }
        rows.push({
          subjectUserId: result.userId,
          displayName: profile.displayName,
          avatarAssetId: profile.avatar?.assetId ?? null,
          profileVersion: profile.profileVersion,
          availability: 'ok',
          cachedAtMs: Date.now(),
        });
        continue;
      }
      if (request.minProfileVersion > 0 &&
          (!Number.isSafeInteger(result.profileVersion) ||
           result.profileVersion! < request.minProfileVersion)) {
        throw new Error('IM-driven unavailable identity is missing its canonical version');
      }
      if (result.profileVersion == null) {
        // The response is complete, but a nonexistent ID has no canonical
        // version. It may be short-lived request-negative cached in memory,
        // never written as a durable identity watermark.
        continue;
      }
      rows.push({
        subjectUserId: result.userId,
        displayName: null,
        avatarAssetId: null,
        profileVersion: result.profileVersion,
        availability: 'unavailable',
        cachedAtMs: Date.now(),
      });
    }
    return rows;
  }

  private toStoreIdentity(
    row: DBUserIdentity,
    avatar: UserIdentity['avatar'],
    source: UserIdentity['source'],
  ): UserIdentity {
    return {
      userId: row.subjectUserId,
      displayName: row.displayName,
      avatarAssetId: row.avatarAssetId,
      avatar,
      profileVersion: row.profileVersion,
      availability: row.availability,
      source,
    };
  }
}
